import React, { useRef, useState, useEffect, useCallback } from "react";
import styles from "./IntakeFormsGrid.module.css";
import { Row, Col, Button, OverlayTrigger, Tooltip, Badge } from "react-bootstrap";
import * as Icon from "react-bootstrap-icons";
import { ThreeCircles } from "react-loader-spinner";
import CygnusGrid from "../../reusable_components/CygnusGrid/CygnusGrid";
import { CygnusGridRef } from "../../reusable_components/CygnusGrid/CygnusGrid.utils";
import { logout, hasFeature } from "../../../utils";
import { ColDef } from "ag-grid-community";
import CustomButton from "../../../pages/reusable_components/custom_button_component/Button";
import { useGlobalStore } from "../../store/globalStore"
import IntakeFormAIModal from "./IntakeFormAIModal/IntakeFormAIModal";
import { FORM_TYPE_OPTIONS } from "./IntakeFormAIModal/IntakeFormAIModal.types";

const IntakeFormsGrid: React.FC<{ transporter: any }> = ({ transporter }) => {
  console.log("INTAKE FORM GRID: ", transporter);
  const gridRef = useRef<CygnusGridRef>(null);
  const [formData, setFormData] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [key, setKey] = useState<number | undefined>(undefined);
  const [modalShow, setModalShow] = useState<boolean>(false);
  const [modalMode, setModalMode] = useState<"generate" | "modify">("generate");
  const [modifyTarget, setModifyTarget] = useState<{
    formId: number;
    templateId: number;
    formName: string;
    formType: string;
    lineOfService: string;
    currentFieldCodes: string[];
  } | null>(null);

  const { purposeOptions } = useGlobalStore();

  const canUseAIIntakeForm = hasFeature("AI_CLIENT_INTAKE_FORM_GENERATION");

  useEffect(() => localStorage.setItem("url", "myforms"), []);

  const getLobLabel = useCallback(
    (lobCode: string) => {
      if (!lobCode) return "—";
      const found = purposeOptions?.find(
        (opt: any) => opt.value === lobCode || opt.code === lobCode
      );
      return found?.label || lobCode;
    },
    [purposeOptions]
  );

  const fetchForms = useCallback(async () => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URI}/mdm/forms/`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        if (response.status === 401) {
          logout();
          alert("Session ended. Please log in again.");
        }
        throw new Error("Failed to fetch forms.");
      }

      const data = await response.json();
      setFormData(data?.data ?? []);
      setKey(data?.data.length);
    } catch (error) {
      console.error("Error fetching forms:", error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchForms();
  }, [fetchForms]);

  const columnDefs: ColDef[] = [
    { field: "FormName", headerName: "Form Name", flex: 1 },
    { field: "FormPurpose", headerName: "Form Type", flex: 1 },
    {
      field: "LineOfServiceCode",
      headerName: "Line of Service",
      flex: 1,
      valueFormatter: (params) => getLobLabel(params.value),
    },
    {
      field: "Status",
      headerName: "Status",
      width: 120,
      maxWidth: 120,
      cellRenderer: (params: any) => {
        const isActive = params.value === "Active";
        return (
          <Badge bg={isActive ? "success" : "secondary"}>
            {params.value}
          </Badge>
        );
      },
    },
    {
      headerName: "Actions",
      field: "actions",
      cellRenderer: (params: any) => {
        const form = params.data;

        const handleEdit = () => {
          transporter("onboarding_form_editor", {
            onboarding_form_config_option: {
              lineOfService: getLobLabel(form.LineOfServiceCode),
              lineOfServiceCode: form.LineOfServiceCode,
              createform_option: {
                id: form.FirmFormId,
                templateid: form.FirmFormsTemplateId,
                form_name: form.FormName,
                form_purpose: form.FormTypeCode,
                line_of_service: form.LineOfServiceCode,
                initial_data: form.InputStructure,
                is_final: form.Status === "Active",
              },
            },
          });
        };

        const isDisabled = form.Status === "Processing" || form.Status === "Failed";
        const disabledTooltip =
          form.Status === "Processing"
            ? "Form is currently processing"
            : "Form generation failed";

        return (
          <div className={styles.actionsCell}>
            <OverlayTrigger
              placement="bottom"
              overlay={
                <Tooltip id="button-tooltip">
                  {isDisabled ? disabledTooltip : "Edit form structure"}
                </Tooltip>
              }
            >
              <span style={{ cursor: isDisabled ? "not-allowed" : "auto" }}>
                <Button
                  variant="link"
                  onClick={handleEdit}
                  disabled={isDisabled}
                  style={{ padding: 0, pointerEvents: isDisabled ? "none" : "auto" }}
                >
                  <Icon.PencilSquare
                    color={isDisabled ? "var(--bs-secondary)" : "var(--theme1_primary)"}
                  />
                </Button>
              </span>
            </OverlayTrigger>
            {canUseAIIntakeForm && (
              <OverlayTrigger
                placement="bottom"
                overlay={
                  <Tooltip id="button-tooltip">
                    {isDisabled ? disabledTooltip : "Modify with AI"}
                  </Tooltip>
                }
              >
                <span style={{ cursor: isDisabled ? "not-allowed" : "auto" }}>
                  <Button
                    variant="link"
                    disabled={isDisabled}
                    style={{ padding: 0, pointerEvents: isDisabled ? "none" : "auto" }}
                    onClick={() => {
                      setModifyTarget({
                        formId: form.FirmFormId,
                        templateId: form.FirmFormsTemplateId,
                        formName: form.FormName,
                        formType: "Intake",
                        lineOfService: form.LineOfServiceCode,
                        currentFieldCodes: (form.InputStructure?.fields ?? [])
                          .map((f: any) => f.allfields_code)
                          .filter(Boolean),
                      });
                      setModalMode("modify");
                      setModalShow(true);
                    }}
                  >
                    <Icon.Magic
                      color={isDisabled ? "var(--bs-secondary)" : "var(--theme1_primary)"}
                    />
                  </Button>
                </span>
              </OverlayTrigger>
            )}
          </div>
        );
      },
      width: 80,
      maxWidth: 80,
      filter: false,
      pinned: "right",
    },
  ];

  return (
    <>
      <IntakeFormAIModal
        mode={modalMode}
        show={modalShow}
        onHide={() => {
          setModalShow(false);
          setModifyTarget(null);
        }}
        onDone={fetchForms}
        formId={modifyTarget?.formId}
        templateId={modifyTarget?.templateId}
        formType={modifyTarget?.formType}
        currentFieldCodes={modifyTarget?.currentFieldCodes}
        initialConfig={modifyTarget ? {
          formName: modifyTarget.formName ?? "",
          formType: FORM_TYPE_OPTIONS.find((o) => o.value === modifyTarget.formType) ?? null,
          lineOfService:
            purposeOptions.find(
              (o: any) =>
                o.value === modifyTarget.lineOfService ||
                o.code === modifyTarget.lineOfService
            ) ?? null,
          instructions: "",
        } : undefined}
      />

      <Row className="no-gutter">
        <Col md={12} className={styles.headerWrapper}>
          <Row>
            <Col md={12}>
              <p className="display-6">Client Intake Forms</p>
            </Col>
          </Row>

          <Row>
            <Col md={12} className={styles.actionsRow}>
              {canUseAIIntakeForm && (
                <CustomButton
                  onclick={() => { setModalMode("generate"); setModalShow(true); }}
                  content="Generate Intake Form"
                  type="secondary"
                />
              )}
              <CustomButton
                onclick={() =>
                  transporter("onboarding_form_editor", {
                    onboarding_form_config_option: {},
                  })
                }
                content="Create Form"
              />
              <div className={styles.refreshContainer}>
                {isLoading ? (
                  <ThreeCircles
                    height="20"
                    width="20"
                    visible={true}
                    ariaLabel="three-circles-rotating"
                    outerCircleColor="var(--theme1_tertiary)"
                    innerCircleColor="var(--theme1_secondary)"
                    middleCircleColor="var(--theme1_primary)"
                  />
                ) : (
                  <Icon.ArrowCounterclockwise
                    onClick={fetchForms}
                    className={styles.refreshIcon}
                  />
                )}
              </div>
            </Col>
          </Row>
        </Col>
        <Col md={12} className={styles.formsGridContainer}>
          <CygnusGrid
            key={key}
            ref={gridRef}
            columnDefs={columnDefs}
            fetchData={async () => {
              return { records: formData, totalCount: formData.length };
            }}
            rowModelType="clientSide"
            rowSelection="single"
            dataIdentifier="FirmFormId"
            onLoadingChange={setIsLoading}
            cachedIsLoading={isLoading}
            cachedData={formData}
            setCachedData={setFormData}
          />
        </Col>
      </Row>
    </>
  );
};

export default IntakeFormsGrid;
